import React, { JSX, useContext, useEffect, useState } from "react";
import { isEqual, orderBy } from "lodash";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { ColumnConfig, SortDirections, printNumberAsString, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { TextAlignment, WafflesThemes } from "@app/shared/waffles/exports";
import IconToggle from "@app/shared/IconToggle";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESButton } from "@app/shared/qes-standards/Button";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { BenchmarkingNetworkCounty, BenchmarkingNetwork, BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared";

interface NetworkFlyoutCountiesTableProps {
  data: BenchmarkingNetworkCounty[];
  contract: BenchmarkingNetwork;
  isYourNetwork: boolean;
  loading: boolean;
  onApplyServiceAreaClick: (countyIds: number[], contracts: BenchmarkingNetwork[]) => void;
}

interface NetworkFlyoutCountiesTableData {
  countyName: string;
  countyStatus: RegionSetStatusType;
  membershipCount: string;
  stateAbbreviation: string;
  [wafflesKeyColumnName]: string;
}

const NetworkFlyoutCountiesTable = (props: NetworkFlyoutCountiesTableProps): JSX.Element => {
  const [allActiveCountiesChecked, setAllActiveCountiesChecked] = useState<boolean>(false);
  const [allCountiesDisplayed, setAllCountiesDisplayed] = useState<boolean>(false);
  const [countiesLoading, setCountiesLoading] = useState<boolean>(false);
  const [tableData, setTableData] = useState<NetworkFlyoutCountiesTableData[]>([]);

  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);

  useEffect(() => {
    if (!!props.data?.length) {
      const countiesEqual = isEqual(
        props.data.map(x => x.countyId).sort((a, b) => a - b),
        benchmarkingFiltersContext.displayedPrimaryFilters.countyIds.sort((a, b) => a - b)
      );

      if (countiesEqual) {
        setAllCountiesDisplayed(true);
      }
    }
  }, [props.data]);

  useEffect(() => {
    if (!!props.data) {
      let data: BenchmarkingNetworkCounty[];

      if (allActiveCountiesChecked) {
        data = props.data;
      } else {
        data = props.data.filter(x => benchmarkingFiltersContext.displayedPrimaryFilters.countyIds.includes(x.countyId));
      }

      /* oxlint-disable sort-keys */
      let newTableData: NetworkFlyoutCountiesTableData[] = data.map((county) => {
        return {
          [wafflesKeyColumnName]: county.countyId.toString(),
          countyName: county.countyName,
          stateAbbreviation: county.stateAbbreviation,
          countyStatus: county.countyStatus,
          membershipCount: county.membershipCount === 0 ? '*' : printNumberAsString(county.membershipCount, "---"),
        }
      });
      /* oxlint-enable sort-keys */

      // if the network is from "your qes networks,"
      // first sort by county status, then by county name
      if (props.isYourNetwork) {
        const sortOrder: RegionSetStatusType[] = [
          RegionSetStatusType.Active,
          RegionSetStatusType.Approved,
          RegionSetStatusType.Pending,
          RegionSetStatusType.Opportunity,
        ];

        newTableData = newTableData.sort((a, b) => {
          return sortOrder.indexOf(a.countyStatus) - sortOrder.indexOf(b.countyStatus) ||
            a.countyName.localeCompare(b.countyName);
        });
      }

      setTableData(newTableData);

      if (countiesLoading) {
        setCountiesLoading(false);
      }
    }
  }, [props.data, allActiveCountiesChecked]);

  const onShowAllCountiesToggle = (): void => {
    setAllActiveCountiesChecked(!allActiveCountiesChecked);
    setCountiesLoading(true);
  }

  const columnConfig: ColumnConfig = {
    countyName: {
      alignment: TextAlignment.Left,
      displayText: "County Name",
      noResize: true,
      sizeContentToFit: true,
      testId: "county-name",
    },
    countyStatus: {
      alignment: TextAlignment.Left,
      displayText: "County Status",
      hidden: !props.isYourNetwork || benchmarkingFiltersContext.selectedLineOfBusiness === BenchmarkingLineOfBusinessEnum.Medicaid,
      noResize: true,
      sizeContentToFit: true,
      testId: "county-status",
    },
    membershipCount: {
      alignment: TextAlignment.Left,
      displayText: "Membership Count",
      hidden: [BenchmarkingLineOfBusinessEnum.Marketplace, BenchmarkingLineOfBusinessEnum.Medicaid].includes(benchmarkingFiltersContext.selectedLineOfBusiness),
      noResize: true,
      sizeContentToFit: true,
      testId: "membership-count",
    },
    stateAbbreviation: {
      alignment: TextAlignment.Left,
      displayText: "State",
      noResize: true,
      sizeContentToFit: true,
      testId: "state-abbreviation",
    },
  };

  const customSort = (
    records: NetworkFlyoutCountiesTableData[],
    column: keyof NetworkFlyoutCountiesTableData,
    sortDirection: SortDirections
  ): NetworkFlyoutCountiesTableData[] => {
    let sortedData: NetworkFlyoutCountiesTableData[] = [];

    switch (column) {
      case "countyName":
      case "countyStatus":
      case "stateAbbreviation":
        sortedData = orderBy(records, (d) => d[column], sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
      case "membershipCount":
        sortedData = orderBy(records, (d) => d.membershipCount === '*' ? 0 : parseInt(d.membershipCount.replace(/,/g, "")), sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
      default:
        break;
    }

    return sortedData;
  }

  const getIconToggle = (): JSX.Element => {
    const getCheckboxLabel = (): string => {
      switch (benchmarkingFiltersContext.displayedPrimaryFilters.lineOfBusinessId) {
        case BenchmarkingLineOfBusinessEnum.Medicare:
          return "Show all counties for this contract";
        case BenchmarkingLineOfBusinessEnum.Marketplace:
          return "Show all counties for this network";
        case BenchmarkingLineOfBusinessEnum.Medicaid:
          return "Show all counties for this program";
        default:
          return "Show all counties";
      }
    }

    return (
      <IconToggle
        active={allActiveCountiesChecked}
        disabled={props.loading || !tableData.length || countiesLoading || allCountiesDisplayed}
        onToggle={() => onShowAllCountiesToggle()}
        activeIconClass={["fa", "fa-toggle-on"]}
        inActiveIconClass={["fa", "fa-toggle-off"]}
        baseClassName={["icon-toggle", "p-0", "mb-0"]}
      >
        {getCheckboxLabel()}
      </IconToggle>
    );
  }

  return (
    <React.Fragment>
      <div className="row">
        <div className="col-12">
          <div className="d-flex align-items-center mb-3">
            <div className="d-inline-block">
              {allCountiesDisplayed ? (
                <TooltipWrapper
                  text="All counties are already displayed"
                >
                  {getIconToggle()}
                </TooltipWrapper>
              ) : (
                getIconToggle()
              )}
            </div>

            <QESButton
              disabled={props.loading || !tableData.length || countiesLoading}
              qesTestId={`network-benchmarking-dashboard--h-contract-flyout__set-service-area-button-${props.contract?.contractId}`}
              qesType="secondary"
              onClick={() => props.onApplyServiceAreaClick(tableData.map(x => parseInt(x.key)), [props.contract])}
              className="p-0 ms-3"
            >
              Set service area filter
            </QESButton>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12 dashboard__scrollable">
          <Waffles<NetworkFlyoutCountiesTableData>
            columns={columnConfig}
            customSort={customSort}
            data={tableData}
            fullWidth={true}
            loading={countiesLoading}
            testId={`network-benchmarking-dashboard--h-contract-flyout__counties-table-${props.contract?.contractId}`}
            theme={WafflesThemes.Simple}
          />
        </div>
      </div>
    </React.Fragment>
  )
}

export default NetworkFlyoutCountiesTable;
